import { useState } from 'react';

import type { CadenceStepTypePure } from '../CGStep/types';
import type { StepHistoryOption, StepResolutionState } from './stepResolution';
import { Button, Radio } from '../Input2';
import Popup from '../Popup/Popup';
import { General, Media } from '../StrokeIcons';
import { canLinkHistoryToStepType, historyOptionsForStepType } from './stepResolution';

/** Radio value for "don't link anything" — history ids are uuids, so it can't collide. */
const NO_LINK = '';

const COPY: Record<StepResolutionState, { title: string; body: string; confirm: string }> = {
  finished: {
    title: 'Mark step as finished',
    body: 'Use this when you did it yourself. The campaign moves on to the next step.',
    confirm: 'Mark finished',
  },
  skipped: {
    title: 'Skip this step',
    body: "It won't run. The campaign moves on to the next step.",
    confirm: 'Skip step',
  },
};

export type ResolveStepPopupProps = {
  state: StepResolutionState;
  /** The step being closed out — only history of its own kind is offered (a call step lists calls). */
  stepType: CadenceStepTypePure;
  /** The contact's linkable history rows, newest first; the popup narrows them to `stepType`. */
  historyOptions: StepHistoryOption[];
  /** `interactionId` is the chosen history row, or `undefined` when the rep links nothing. */
  onConfirm: (interactionId: string | undefined) => void;
  onClose: () => void;
};

/**
 * Confirms closing out a campaign step by hand (finished / skipped) and lets the rep attribute an
 * existing history item to it — e.g. the LinkedIn message they sent outside the automation.
 */
export function ResolveStepPopup({ state, stepType, historyOptions, onConfirm, onClose }: ResolveStepPopupProps) {
  const [selectedId, setSelectedId] = useState(NO_LINK);
  const copy = COPY[state];
  const matchingOptions = historyOptionsForStepType(historyOptions, stepType);

  return (
    <Popup onClose={onClose} className="w-full max-w-md text-left">
      <div className="flex flex-col gap-1" data-testid="resolve-step-popup" data-state={state}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{copy.title}</h2>
        <p className="text-sm text-lynch-600 dark:text-lynch-300">{copy.body}</p>
      </div>

      {/* Steps with no outreach of their own (sift, switch campaign) have nothing to link. */}
      {canLinkHistoryToStepType(stepType) && (
        <div className="flex flex-col gap-1.5" data-testid="resolve-step-link-section">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-lynch-500 dark:text-lynch-400">
            Link a history item (optional)
          </span>
          {matchingOptions.length === 0 ? (
            <p className="text-xs italic text-lynch-500 dark:text-lynch-400" data-testid="resolve-step-no-history">
              No matching history to link yet.
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto">
              <Radio
                data-testid="resolve-step-history"
                size="sm"
                name="resolve-step-history"
                className="w-full"
                value={selectedId}
                onChange={setSelectedId}
                options={[
                  { value: NO_LINK, label: "Don't link anything" },
                  ...matchingOptions.map((o) => ({ value: o.id, label: o.label })),
                ]}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-1.5">
        <Button size="sm" text onClick={onClose} data-testid="resolve-step-cancel">
          Cancel
        </Button>
        <Button
          size="sm"
          color="blue"
          icon={state === 'finished' ? <General.CheckCircle /> : <Media.SkipForward />}
          onClick={() => onConfirm(selectedId === NO_LINK ? undefined : selectedId)}
          data-testid="resolve-step-confirm">
          {copy.confirm}
        </Button>
      </div>
    </Popup>
  );
}
